import {
  useBinnedDataBufferCache,
  useCdfDataFetcher,
} from 'features/dataLayers'
import type { GeckoBinnedDataBuffer } from 'features/dataLayers/geckoBinnedDataBuffer'
import {
  createCdfParquetBlob,
  getGeneratedCdfParquetColumnName,
  getGeneratedCdfParquetFileName,
} from 'features/dataLayers/generation/generatedCdfParquet'
import { DuckDbInstanceContext } from 'features/duckDB'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useRolodexCache } from 'features/rolodex'
import memoize from 'lodash/memoize'
import { useCallback, useContext } from 'react'
import { GeckoComponentDataFrame } from 'types'

/**
 * Persist a generated data layer as its own self-contained CDF entry, with
 * its parquet file uploaded onto that entry — nothing is written to the QA'd
 * component-dataset. Canonical layers carry a deterministic id per (kind,
 * component, inspection), so re-running a workflow upserts the same entry.
 *
 * The entry is the CDF's raw JSON, unchanged: no parquet metadata (the file
 * and column are derived from the id, and the absence of
 * `_parquet_column_name` routes `useCdfDataGenerator` to the CDF's own
 * entry), and the inherited `component-dataset` tag stays so the details
 * page can group layers into dataset families.
 */
export function usePersistGeneratedCdf(): (
  cdf: GeckoComponentDataFrame,
  binnedDataBuffer?: GeckoBinnedDataBuffer
) => Promise<GeckoComponentDataFrame> {
  const cdfDataFetcher = useCdfDataFetcher()
  const { putBinnedDataBufferInCache } = useBinnedDataBufferCache()
  const rolodexCache = useRolodexCache()
  const { rolodexPut, rolodexUploadFile } = useRolodexEdit()
  const { ensureDuckDbInstance } = useContext(DuckDbInstanceContext)

  return useCallback(
    async (
      cdf: GeckoComponentDataFrame,
      providedBinnedDataBuffer?: GeckoBinnedDataBuffer
    ) => {
      const binnedDataBuffer =
        providedBinnedDataBuffer ?? (await cdfDataFetcher(cdf))
      if (!binnedDataBuffer) {
        throw new Error(`No generated CDF data was available for ${cdf.id}.`)
      }

      const columnKey = getGeneratedCdfParquetColumnName(cdf.id)
      const parquetFileName = getGeneratedCdfParquetFileName(cdf.id)

      try {
        const db = await ensureDuckDbInstance()
        const parquetBlob = await createCdfParquetBlob(
          db,
          binnedDataBuffer,
          columnKey,
          cdf.id
        )
        await rolodexPut([cdf.raw])
        await rolodexUploadFile(cdf.id, parquetBlob, parquetFileName)
      } catch (error) {
        throw new Error(
          `Failed to persist generated data layer ${cdf.id}.${formatRolodexError(error)}`
        )
      }

      rolodexCache.putEntry(cdf.raw)

      const memoizedGetterFn = memoize(async () => binnedDataBuffer)
      putBinnedDataBufferInCache({
        id: cdf.id,
        generation: cdf.generation,
        data: memoizedGetterFn,
      })

      return cdf
    },
    [
      cdfDataFetcher,
      ensureDuckDbInstance,
      putBinnedDataBufferInCache,
      rolodexCache,
      rolodexPut,
      rolodexUploadFile,
    ]
  )
}

function formatRolodexError(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (
      error as {
        response?: {
          status?: number
          data?: unknown
        }
      }
    ).response
    const responseDetails =
      response?.data === undefined ? '' : ` ${formatErrorData(response.data)}`

    if (response?.status) {
      return ` Rolodex responded with ${response.status}.${responseDetails}`
    }

    if (responseDetails) {
      return responseDetails
    }
  }

  if (error instanceof Error && error.message) {
    return ` ${error.message}`
  }

  return ''
}

function formatErrorData(data: unknown): string {
  if (typeof data === 'string') return data

  try {
    return JSON.stringify(data)
  } catch {
    return String(data)
  }
}
